import { CommonModule, DatePipe } from '@angular/common';
import { Component, DestroyRef, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { switchMap } from 'rxjs';
import { I18nService } from '../core/i18n.service';
import { SessionService } from '../core/session.service';
import { TeamsApiService } from '../core/teams-api.service';
import { PlayerTeamSummary } from '../core/teams-api.types';
import { TournamentRegistrationsApiService } from '../core/tournament-registrations-api.service';
import { TournamentRegistrationItem } from '../core/tournament-registrations-api.types';
import { TournamentsApiService } from '../core/tournaments-api.service';
import { TournamentPublic } from '../core/tournaments-api.types';

@Component({
  selector: 'app-tournament-registration-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, DatePipe],
  template: `
    <section class="page">
      <p><a [routerLink]="['/tournaments', slug()]">{{ t('reg.back') }}</a></p>
      <h1>{{ t('reg.title') }}</h1>
      <p class="hint">{{ t('reg.subtitle') }}</p>

      @if (pageLoading()) {
        <p class="state">{{ t('reg.pageLoading') }}</p>
      } @else {
        @if (tournament(); as tournamentData) {
          <article class="card summary">
            <p><strong>{{ tournamentData.name }}</strong> - {{ tournamentData.game }}</p>
            <p class="meta">{{ t('reg.start', { value: (tournamentData.starts_at | date: 'short') ?? '' }) }}</p>
            <p class="meta">{{ t('reg.close', { value: (tournamentData.registration_closes_at | date: 'short') ?? '' }) }}</p>
          </article>
        }

        <section class="card">
          <h2>{{ t('reg.actionStateTitle') }}</h2>
          <p class="state state--strong">{{ actionStateLabel() }}</p>
          <p class="state"><strong>{{ t('reg.actionReasonTitle') }}</strong> {{ actionReasonText() }}</p>
          <p class="state"><strong>{{ t('reg.nextStepTitle') }}</strong> {{ actionNextStepText() }}</p>
        </section>

        <section class="card">
          <h2>{{ t('reg.actionTitle') }}</h2>
          @if (registrationClosed()) {
            <p class="notice notice--warn">{{ t('reg.closed') }}</p>
          } @else if (existingActiveRegistration(); as existing) {
            <p class="notice notice--info">{{ t('reg.already', { team: existing.team_name, status: formatRegistrationStatus(existing.status) }) }}</p>
            <p class="state">{{ t('reg.noNeed') }}</p>
          } @else if (manageableTeamsLoading()) {
            <p class="state">{{ t('reg.teamsLoading') }}</p>
          } @else if (manageableTeams().length === 0) {
            <p class="notice notice--info">{{ t('reg.noManageable') }}</p>
            <p class="state">{{ t('reg.nextAction') }} <a routerLink="/player/teams/create">{{ t('reg.createTeamLink') }}</a>.</p>
          } @else {
            <form (ngSubmit)="register()" #form="ngForm" class="form">
              <label>{{ t('reg.teamLabel') }}
                <select name="teamId" required [(ngModel)]="selectedTeamId">
                  <option [ngValue]="null">{{ t('reg.teamSelect') }}</option>
                  @for (team of manageableTeams(); track team.id) {
                    <option [ngValue]="team.id">{{ team.name }} ({{ team.member_count }}/{{ team.max_members }})</option>
                  }
                </select>
              </label>
              <div class="actions">
                <button type="submit" [disabled]="shouldDisableRegisterAction() || !form.valid || !selectedTeamId">
                  {{ registerLoading() ? t('reg.submitting') : t('reg.submit') }}
                </button>
              </div>
            </form>
          }

          @if (registerSuccess()) {
            <p class="state state--success">{{ registerSuccess() }}</p>
          }
          @if (registerError()) {
            <p class="state state--error">{{ registerError() }}</p>
          }
        </section>

        <section class="card">
          <h2>{{ t('reg.listTitle') }}</h2>
          @if (registrationsLoading()) {
            <p class="state">{{ t('reg.listLoading') }}</p>
          } @else if (registrationsError()) {
            <p class="state state--error">{{ registrationsError() }}</p>
          } @else if (registrations().length === 0) {
            <p class="state">{{ t('reg.listEmpty') }}</p>
          } @else {
            <ul class="list">
              @for (registration of registrations(); track registration.id) {
                <li class="item">
                  <p><strong>{{ registration.team_name }}</strong> - {{ t('reg.status', { value: formatRegistrationStatus(registration.status) }) }}</p>
                  <p>{{ t('reg.submittedAt', { value: (registration.submitted_at | date: 'short') ?? '' }) }}</p>
                  @if (registration.cancelled_at) {
                    <p>{{ t('reg.cancelledAt', { value: (registration.cancelled_at | date: 'short') ?? '' }) }}</p>
                  }
                  @if (registration.check_in_state === 'checked_in') {
                    <p class="state state--success">{{ t('reg.checkIn.ok') }}</p>
                  } @else if (registration.check_in_state === 'eligible') {
                    <p class="state">{{ t('reg.checkIn.eligible') }}</p>
                  } @else if (registration.check_in_state === 'window_closed') {
                    <p class="state">{{ t('reg.checkIn.closed') }}</p>
                  } @else {
                    <p class="state">{{ t('reg.checkIn.notEligible', { value: formatRegistrationStatus(registration.status) }) }}</p>
                  }
                  @if (registration.can_check_in) {
                    <button type="button" (click)="checkIn(registration)" [disabled]="checkInLoadingId() === registration.id">
                      {{ checkInLoadingId() === registration.id ? t('reg.checkIn.loading') : t('reg.checkIn.do') }}
                    </button>
                  }
                  @if (registration.can_cancel) {
                    <button type="button" class="danger" (click)="cancel(registration)" [disabled]="cancelLoadingId() === registration.id">
                      {{ cancelLoadingId() === registration.id ? t('reg.cancel.loading') : t('reg.cancel') }}
                    </button>
                  }
                </li>
              }
            </ul>
          }
        </section>

        <section class="card quick-links">
          <h2>{{ t('reg.quickLinks') }}</h2>
          <div class="quick-links__row">
            <a routerLink="/player/teams">{{ t('reg.quick.toTeams') }}</a>
            <a [routerLink]="['/tournaments', slug(), 'bracket']">{{ t('reg.quick.toBracket') }}</a>
            <a [routerLink]="['/tournaments', slug()]">{{ t('reg.quick.toTournament') }}</a>
          </div>
        </section>
      }
    </section>
  `,
  styles: `
    .page { padding: 1.2rem; max-width: 820px; margin: 0 auto; }
    h1 { margin: 0.2rem 0 0.5rem; font-size: 1.4rem; color: var(--text-strong); }
    h2 { margin: 0 0 0.6rem; font-size: 1.1rem; color: var(--text-strong); }
    .hint { margin: 0 0 0.9rem; color: var(--text-muted); }
    .summary p { margin: 0.15rem 0; }
    .meta { margin: 0; color: var(--text-muted); }
    .card { border: 1px solid var(--line-soft); border-radius: 12px; background: linear-gradient(180deg, rgba(23, 36, 58, 0.94) 0%, rgba(18, 29, 47, 0.94) 100%); padding: 0.9rem; margin-bottom: 0.8rem; box-shadow: var(--shadow-soft); }
    .form { display: grid; gap: 0.7rem; }
    label { display: grid; gap: 0.35rem; font-size: 0.92rem; color: #dbe9ff; }
    input, select { padding: 0.55rem 0.6rem; border: 1px solid var(--line-soft); border-radius: 8px; background: rgba(13, 21, 34, 0.9); color: var(--text-strong); }
    .actions { display: flex; gap: 0.5rem; }
    button { border: 1px solid rgba(138, 184, 255, 0.6); border-radius: 8px; padding: 0.55rem 0.8rem; background: linear-gradient(180deg, var(--accent) 0%, var(--accent-strong) 100%); color: #091325; font-weight: 700; }
    .secondary { background: rgba(101, 145, 216, 0.18); color: #d7e7ff; border-color: rgba(112, 164, 255, 0.42); }
    .danger { background: rgba(172, 45, 45, 0.2); color: #ffdede; border: 1px solid rgba(255, 120, 120, 0.5); margin-top: 0.3rem; }
    .list { list-style: none; margin: 0; padding: 0; display: grid; gap: 0.6rem; }
    .item { border: 1px solid var(--line-soft); border-radius: 10px; padding: 0.7rem; background: rgba(14, 23, 37, 0.82); }
    .item p { margin: 0.2rem 0; color: var(--text-muted); }
    .item strong { color: var(--text-strong); }
    .state { margin: 0.55rem 0 0; color: var(--text-muted); }
    .state--success { color: var(--ok-text); }
    .state--error { color: var(--danger); }
    .notice { margin: 0 0 0.55rem; border-radius: 8px; padding: 0.55rem 0.65rem; }
    .notice--warn { background: rgba(88, 53, 15, 0.5); color: #ffd9a8; border: 1px solid rgba(201, 132, 40, 0.55); }
    .notice--info { background: rgba(47, 69, 101, 0.52); color: #d5e6ff; border: 1px solid rgba(112, 164, 255, 0.42); }
    .state--strong { font-weight: 700; color: #d9e8ff; }
    .quick-links__row { display: flex; flex-wrap: wrap; gap: 0.55rem; }
    .quick-links a { border: 1px solid rgba(112, 164, 255, 0.42); border-radius: 8px; padding: 0.45rem 0.65rem; background: rgba(101, 145, 216, 0.18); color: #d7e7ff; text-decoration: none; font-weight: 700; }
    a { color: var(--accent); text-decoration: none; font-weight: 600; }
  `
})
export class TournamentRegistrationPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly tournamentApi = inject(TournamentsApiService);
  private readonly registrationsApi = inject(TournamentRegistrationsApiService);
  private readonly teamsApi = inject(TeamsApiService);
  private readonly i18n = inject(I18nService);
  private readonly session = inject(SessionService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly slug = signal('');
  protected readonly tournament = signal<TournamentPublic | null>(null);

  protected readonly pageLoading = signal(true);
  protected selectedTeamId: number | null = null;
  protected readonly manageableTeams = signal<PlayerTeamSummary[]>([]);
  protected readonly manageableTeamsLoading = signal(false);
  protected readonly registerLoading = signal(false);
  protected readonly cancelLoadingId = signal<number | null>(null);
  protected readonly checkInLoadingId = signal<number | null>(null);
  protected readonly registrationsLoading = signal(false);

  protected readonly registerSuccess = signal<string | null>(null);
  protected readonly registerError = signal<string | null>(null);
  protected readonly registrationsError = signal<string | null>(null);

  protected readonly registrations = signal<TournamentRegistrationItem[]>([]);

  constructor() {
    this.route.paramMap
      .pipe(
        switchMap((params) => {
          const slug = params.get('slug') ?? '';
          this.slug.set(slug);
          return this.tournamentApi.getPublicTournament(slug);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: ({ tournament }) => {
          this.tournament.set(tournament);
          this.pageLoading.set(false);
          this.loadManageableTeams();
          this.loadRegistrations();
        },
        error: (error) => {
          if (error?.status === 401) {
            this.handleUnauthorized();
            return;
          }
          this.registerError.set(this.t('reg.loadError'));
          this.pageLoading.set(false);
        }
      });
  }

  protected register(): void {
    if (!this.selectedTeamId) {
      this.registerError.set(this.t('reg.selectTeam'));
      return;
    }

    this.registerLoading.set(true);
    this.registerError.set(null);
    this.registerSuccess.set(null);

    this.registrationsApi
      .create(this.slug(), this.selectedTeamId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ registration }) => {
          this.registerSuccess.set(
            this.t('reg.successRegistered', { team: registration.team_name, status: this.formatRegistrationStatus(registration.status) })
          );
          this.registerLoading.set(false);
          this.loadRegistrations();
        },
        error: (error) => {
          if (error?.status === 401) {
            this.handleUnauthorized();
            return;
          }
          const apiError = error?.error?.error;
          const apiErrors = error?.error?.errors;
          this.registerError.set(
            (typeof apiError === 'string' && apiError) ||
              (Array.isArray(apiErrors) && apiErrors.length > 0 ? apiErrors.join(' | ') : this.t('reg.error'))
          );
          this.registerLoading.set(false);
        }
      });
  }

  private loadManageableTeams(): void {
    this.manageableTeamsLoading.set(true);

    this.teamsApi
      .getMyTeams()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ teams }) => {
          const manageable = teams.filter((team) => team.can_manage_roster);
          this.manageableTeams.set(manageable);
          this.selectedTeamId = manageable[0]?.id ?? null;
          this.manageableTeamsLoading.set(false);
        },
        error: (error) => {
          if (error?.status === 401) {
            this.handleUnauthorized();
            return;
          }
          this.manageableTeams.set([]);
          this.manageableTeamsLoading.set(false);
        }
      });
  }

  protected cancel(registration: TournamentRegistrationItem): void {
    this.cancelLoadingId.set(registration.id);
    this.registerError.set(null);
    this.registerSuccess.set(null);

    this.registrationsApi
      .cancel(this.slug(), registration.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ registration: updated }) => {
          this.cancelLoadingId.set(null);
          this.registerSuccess.set(this.t('reg.successCancelled', { team: updated.team_name }));
          this.loadRegistrations();
        },
        error: (error) => {
          if (error?.status === 401) {
            this.handleUnauthorized();
            return;
          }
          const apiError = error?.error?.error;
          this.registerError.set(typeof apiError === 'string' ? apiError : this.t('reg.cancelError'));
          this.cancelLoadingId.set(null);
        }
      });
  }

  protected checkIn(registration: TournamentRegistrationItem): void {
    this.checkInLoadingId.set(registration.id);
    this.registerError.set(null);
    this.registerSuccess.set(null);

    this.registrationsApi
      .checkIn(this.slug(), registration.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ registration: updated }) => {
          this.registrations.update((items) =>
            items.map((item) => (item.id === updated.id ? updated : item))
          );
          this.registerSuccess.set(this.t('reg.successCheckIn', { team: updated.team_name }));
          this.checkInLoadingId.set(null);
        },
        error: (error) => {
          if (error?.status === 401) {
            this.handleUnauthorized();
            return;
          }
          const apiError = error?.error?.error;
          this.registerError.set(typeof apiError === 'string' ? apiError : this.t('reg.checkInError'));
          this.checkInLoadingId.set(null);
        }
      });
  }

  private loadRegistrations(): void {
    this.registrationsLoading.set(true);
    this.registrationsError.set(null);

    this.registrationsApi
      .listForTournament(this.slug())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ registrations }) => {
          this.registrations.set(registrations);
          this.registrationsLoading.set(false);
        },
        error: (error) => {
          if (error?.status === 401) {
            this.handleUnauthorized();
            return;
          }
          this.registrationsError.set(this.t('reg.listError'));
          this.registrationsLoading.set(false);
        }
      });
  }

  private handleUnauthorized(): void {
    this.session.clearToken();
    void this.router.navigateByUrl('/player/login');
  }

  protected registrationClosed(): boolean {
    const closesAt = this.tournament()?.registration_closes_at;
    if (!closesAt) {
      return false;
    }

    return new Date(closesAt).getTime() < Date.now();
  }

  protected existingActiveRegistration(): TournamentRegistrationItem | null {
    return (
      this.registrations().find((registration) =>
        ['submitted', 'to_fix', 'validated', 'validated_conditionally', 'rejected'].includes(registration.status)
      ) ?? null
    );
  }

  protected shouldDisableRegisterAction(): boolean {
    return (
      this.registerLoading() ||
      this.manageableTeamsLoading() ||
      this.registrationsLoading() ||
      this.registrationClosed() ||
      this.existingActiveRegistration() !== null ||
      this.manageableTeams().length === 0
    );
  }

  protected actionStateLabel(): string {
    if (this.registrationClosed()) {
      return this.t('reg.actionState.closed');
    }
    if (this.existingActiveRegistration()) {
      return this.t('reg.actionState.already');
    }
    if (this.manageableTeamsLoading()) {
      return this.t('reg.teamsLoading');
    }
    if (this.manageableTeams().length === 0) {
      return this.t('reg.actionState.noTeam');
    }
    return this.t('reg.actionState.ready');
  }

  protected actionReasonText(): string {
    if (this.registrationClosed()) {
      return this.t('reg.closed');
    }
    const existing = this.existingActiveRegistration();
    if (existing) {
      return this.t('reg.already', { team: existing.team_name, status: this.formatRegistrationStatus(existing.status) });
    }
    if (this.manageableTeamsLoading()) {
      return this.t('reg.teamsLoading');
    }
    if (this.manageableTeams().length === 0) {
      return this.t('reg.noManageable');
    }
    return this.t('reg.subtitle');
  }

  protected actionNextStepText(): string {
    if (this.registrationClosed()) {
      return this.t('reg.nextStep.closed');
    }
    if (this.existingActiveRegistration()) {
      return this.t('reg.nextStep.already');
    }
    if (this.manageableTeamsLoading()) {
      return this.t('reg.teamsLoading');
    }
    if (this.manageableTeams().length === 0) {
      return this.t('reg.nextStep.noTeam');
    }
    return this.t('reg.nextStep.ready');
  }

  protected formatRegistrationStatus(status: string): string {
    const labels: Record<string, string> = {
      submitted: this.t('reg.status.submitted'),
      to_fix: this.t('reg.status.to_fix'),
      validated: this.t('reg.status.validated'),
      validated_conditionally: this.t('reg.status.validated_conditionally'),
      rejected: this.t('reg.status.rejected'),
      cancelled: this.t('reg.status.cancelled')
    };

    return labels[status] ?? status;
  }
  protected t(key: string, params?: Record<string, string | number | null | undefined>): string {
    return this.i18n.t(key, params);
  }
}
